<script setup>
import { reactive, computed } from 'vue'
import LoaderSvg from '../../components/utiles/LoaderSvg.vue'

const urlGemaGeoserver = process.env.VUE_APP_GEMA_GEOSERVER
const urlCdn = process.env.VUE_APP_CDN_ARCHIVOS

function valorComoMilesONulo(val) {
  return val === null ? 'Sin dato' : val.toLocaleString('en-US')
}

const info = {
  autoadscrita: p =>
    `<p class="m-t-0"><b>${p.nom_mun ? `${p.nom_mun}, ` : ''}${p.nom_ent}</b></p><p class="m-b-0">${[
      `Pob. de 3 años y más: <b>${valorComoMilesONulo(p.pob_3mas)}</b> hab.`,
      `Pob. indígena autoadscrita: <b>${valorComoMilesONulo(p.consi_ind)}</b> hab`,
      `Porcentaje pob. indígena autoadscrita: <b>${valorComoMilesONulo((p.consi_ind / p.pob_3mas) * 100)}</b>%`,
    ].join('<br />')}</p>`,
  enHogares: p =>
    `<p class="m-t-0"><b>${p.nom_mun ? `${p.nom_mun}, ` : ''}${p.nom_ent}</b></p><p class="m-b-0">${[
      `Pob. total: <b>${valorComoMilesONulo(p.pob_total)}</b> hab.`,
      `Pob. indígena en hogares: <b>${valorComoMilesONulo(p.pob_ind_hog)}</b> hab`,
      `Porcentaje pob. indígena en hogares: <b>${valorComoMilesONulo(p.porc_pob_ind_hog)}</b>%`,
    ].join('<br />')}</p>`,
  afromexicana: p =>
    `<p class="m-t-0"><b>${p.nom_mun ? `${p.nom_mun}, ` : ''}${p.nom_ent}</b></p><p class="m-b-0">${[
      `Pob. total: <b>${valorComoMilesONulo(p.pob_total)}</b> hab.`,
      `Pob. indígena afromexicana: <b>${valorComoMilesONulo(p.pob_afro)}</b> hab`,
      `Porcentaje pob. indígena afromexicana: <b>${valorComoMilesONulo(p.porc_pob_afro)}</b>%`,
    ].join('<br />')}</p>`,
}

//Para loader mapa poblacion indigena
const diccionarioCapasLoaderActivo_mapa1 = reactive({
  pciaf_pob_ind_autoadscrita_20_est_a: true,
  pciaf_pob_indig_hogares_20_est_a: true,
})

function alInicioDeCargarCapa_mapa1(idCapa) {
  diccionarioCapasLoaderActivo_mapa1[idCapa] = true
}

function alTerminarDeCargarCapa_mapa1(idCapa) {
  diccionarioCapasLoaderActivo_mapa1[idCapa] = false
}

const loaderActivo_mapa1 = computed(() => {
  return Object.values(diccionarioCapasLoaderActivo_mapa1).some(
    loaderActivo => loaderActivo
  )
})

//Para loader mapa poblacion afromexicana
const diccionarioCapasLoaderActivo_mapa2 = reactive({
  pciaf_pob_afromex_afrodescen_20_est_a: true,
})

function alInicioDeCargarCapa_mapa2(idCapa) {
  diccionarioCapasLoaderActivo_mapa2[idCapa] = true
}

function alTerminarDeCargarCapa_mapa2(idCapa) {
  diccionarioCapasLoaderActivo_mapa2[idCapa] = false
}

const loaderActivo_mapa2 = computed(() => {
  return Object.values(diccionarioCapasLoaderActivo_mapa2).some(
    loaderActivo => loaderActivo
  )
})
</script>

<template>
  <div class="mapas-poblacion">
    <SisdaiMapa :vista="{ extension: [-118.3651, 14.5321, -86.7104, 32.7187] }">
      <template #panel-encabezado-vis>
        <p class="vis-titulo-visualizacion">Población indígena</p>
      </template>

      <template #panel-izquierda-vis>
        <SisdaiLeyenda para="pciaf_pob_ind_autoadscrita_20_est_a" />
        <SisdaiLeyenda para="pciaf_pob_indig_hogares_20_est_a" />
      </template>
      <LoaderSvg v-if="loaderActivo_mapa1" />
      <SisdaiCapaXyz posicion="1" />

      <SisdaiCapaWms
        capa="pciaf_pob_indig_hogares_20_est_a"
        id="pciaf_pob_indig_hogares_20_est_a"
        :globoInformativo="info.enHogares"
        nombre="Población indígena en hogares"
        posicion="2"
        :url="`${urlGemaGeoserver}/wms`"
        @alIniciarCarga="
          () => alInicioDeCargarCapa_mapa1('pciaf_pob_indig_hogares_20_est_a')
        "
        @alFinalizarCarga="
          () => alTerminarDeCargarCapa_mapa1('pciaf_pob_indig_hogares_20_est_a')
        "
      />

      <SisdaiCapaWms
        capa="pciaf_pob_ind_autoadscrita_20_est_a"
        id="pciaf_pob_ind_autoadscrita_20_est_a"
        :globoInformativo="info.autoadscrita"
        nombre="Población indígena autoadscrita"
        posicion="3"
        :url="`${urlGemaGeoserver}/wms`"
        @alIniciarCarga="
          () =>
            alInicioDeCargarCapa_mapa1('pciaf_pob_ind_autoadscrita_20_est_a')
        "
        @alFinalizarCarga="
          () =>
            alTerminarDeCargarCapa_mapa1('pciaf_pob_ind_autoadscrita_20_est_a')
        "
      />

      <template #panel-pie-vis>
        <p class="vis-fuente m-b-0-esc">
          Fuente: Instituto Nacional de los Pueblos Indígenas (2020).
          <i>
            Sistema Nacional de Información y Estadística sobre los Pueblos y
            Comunidades Indígenas y Afromexicanas, con base en: INEGI. Censo de
            Población y Vivienda 2020, cuestionario ampliado aplicado a una
            muestra censal.
          </i>
        </p>

        <a
          :href="`${urlCdn}/mapas/poblacion_indigena.zip`"
          class="boton boton-primario boton-chico"
          target="_blank"
          rel="noopener noreferrer"
          download="poblacion_indigena.zip"
        >
          Descargar datos
          <span class="icono-archivo-descargar" />
        </a>
      </template>
    </SisdaiMapa>

    <SisdaiMapa :vista="{ extension: [-118.3651, 14.5321, -86.7104, 32.7187] }">
      <template #panel-encabezado-vis>
        <p class="vis-titulo-visualizacion">Población afromexicana</p>
      </template>

      <template #panel-izquierda-vis>
        <SisdaiLeyenda
          para="pciaf_pob_afromex_afrodescen_20_est_a"
          :sinControl="true"
        />
      </template>
      <LoaderSvg v-if="loaderActivo_mapa2" />
      <SisdaiCapaXyz posicion="1" />

      <SisdaiCapaWms
        capa="pciaf_pob_afromex_afrodescen_20_est_a"
        id="pciaf_pob_afromex_afrodescen_20_est_a"
        :globoInformativo="info.afromexicana"
        nombre="Población que se autorreconoce como afrodescendiente o afromexicana"
        posicion="2"
        :url="`${urlGemaGeoserver}/wms`"
        @alIniciarCarga="
          () =>
            alInicioDeCargarCapa_mapa2('pciaf_pob_afromex_afrodescen_20_est_a')
        "
        @alFinalizarCarga="
          () =>
            alTerminarDeCargarCapa_mapa2(
              'pciaf_pob_afromex_afrodescen_20_est_a'
            )
        "
      />

      <template #panel-pie-vis>
        <p class="vis-fuente m-b-0-esc">
          Fuente: Instituto Nacional de los Pueblos Indígenas (2020).
          <i>
            Sistema Nacional de Información y Estadística sobre los Pueblos y
            Comunidades Indígenas y Afromexicanas, con base en: INEGI. Censo de
            Población y Vivienda 2020.
          </i>
        </p>

        <a
          :href="`${urlCdn}/mapas/poblacion_afromexicana.zip`"
          class="boton boton-primario boton-chico"
          target="_blank"
          rel="noopener noreferrer"
          download="poblacion_afromexicana.zip"
        >
          Descargar datos
          <span class="icono-archivo-descargar" />
        </a>
      </template>
    </SisdaiMapa>
  </div>
</template>

<style lang="scss">
.mapas-poblacion {
  display: flex;
  flex-direction: column;
  gap: 24px;

  .contenedor-vis-paneles {
    .panel-hencabezado-vis,
    .panel-izquierda-vis,
    .panel-derecha-vis,
    .panel-pie-vis {
      position: relative;
    }

    .panel-pie-vis {
      display: flex !important;
      flex-direction: column;
      align-items: center;
    }

    @include mediaquery('esc') {
      .panel-izquierda-vis,
      .contenido-vis {
        // grid-row-end: span 2 !important; // para que el panel izquierdo use dos renglones
        max-height: 450px;
        height: 450px;

        //la siguiente media query es para que el panel izquierdo y contenido-vis no se vea tan grande en pantallas pequeñas de alto
        @media screen and (max-height: 650px) {
          max-height: 300px;
          height: 300px;
        }
      }

      .panel-pie-vis {
        // grid-column-start: 2 !important; // para que el pie empiece en la segunda columna
        flex-direction: row;
        gap: 16px;
        overflow-y: hidden; // para que no se vea el scroll en el pie (revisar si es necesario)

        p {
          min-height: 90px;
        }

        a.boton {
          white-space: nowrap;
          height: fit-content;
        }
      }
    }
  }

  @include mediaquery('esc') {
    flex-direction: row;
    align-items: stretch;
    min-height: 520px;
    .sisdai-mapa {
      //height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }
}
</style>
